import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import {
  INGREDIENT_CATEGORIES,
  validateIngredientInput,
  type Ingredient,
  type IngredientCategory,
} from '@muddle/core';
import { api, ApiError } from '../api';
import { useAsync } from '../hooks/useAsync';
import { useTitle } from '../shortcuts';
import { ConfirmDialog, ErrorMessage, PageHeader, Skeleton, useToast } from '../components';

interface Draft {
  id: string | null;
  name: string;
  category: IngredientCategory;
  abv: string;
  notes: string;
}
const emptyDraft = (category: IngredientCategory): Draft => ({
  id: null,
  name: '',
  category,
  abv: '',
  notes: '',
});

const TAB_LABEL: Record<IngredientCategory, string> = {
  spirit: 'Spirits',
  liqueur: 'Liqueurs & wines',
  bitter: 'Bitters',
  citrus: 'Citrus',
  syrup: 'Syrups',
  other: 'Other',
};

export function IngredientsPage() {
  const toast = useToast();
  useTitle('Ingredients');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);
  const [state, reload] = useAsync(() => api.listIngredients(debounced), [debounced]);
  const [tab, setTab] = useState<IngredientCategory | 'all'>('all');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState<Ingredient | null>(null);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const shown =
    state.status === 'ready' ? state.data.filter((i) => tab === 'all' || i.category === tab) : [];

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (draft && !d.open) d.showModal();
    if (!draft && d.open) d.close();
  }, [draft]);

  const open = (i?: Ingredient) => {
    setErrors({});
    setDraft(
      i
        ? { id: i.id, name: i.name, category: i.category, abv: String(i.abvPct), notes: i.notes }
        : emptyDraft(tab === 'all' ? 'spirit' : tab),
    );
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    const v = validateIngredientInput({
      name: draft.name,
      category: draft.category,
      abvPct: draft.abv,
      notes: draft.notes,
    });
    if (!v.value) {
      setErrors(v.errors);
      return;
    }
    setBusy(true);
    try {
      if (draft.id) await api.updateIngredient(draft.id, v.value);
      else await api.createIngredient(v.value);
      toast(draft.id ? 'Ingredient updated' : 'Ingredient added');
      setDraft(null);
      reload();
    } catch (err) {
      setErrors(
        err instanceof ApiError
          ? { name: err.message, ...err.fields }
          : { name: 'Something went wrong.' },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        kicker="Ingredients"
        title="Ingredient library"
        sub="Your spirits, liqueurs and mixers. Edit any name or ABV: it feeds every strength estimate."
        actions={
          <button className="btn btn-primary" type="button" onClick={() => open()}>
            <Plus size={16} aria-hidden /> Add {tab === 'spirit' ? 'spirit' : 'ingredient'}
          </button>
        }
      />
      <div className="field">
        <label className="sr-only" htmlFor="iq">
          Search ingredients
        </label>
        <input
          id="iq"
          type="search"
          placeholder="Search ingredients"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {state.status === 'ready' && (
        <div className="chip-row" role="group" aria-label="Category">
          <button
            type="button"
            className="chip"
            aria-pressed={tab === 'all'}
            onClick={() => setTab('all')}
          >
            All <span className="num">{state.data.length}</span>
          </button>
          {INGREDIENT_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className="chip"
              aria-pressed={tab === c}
              onClick={() => setTab(c)}
            >
              {TAB_LABEL[c]}{' '}
              <span className="num">{state.data.filter((i) => i.category === c).length}</span>
            </button>
          ))}
        </div>
      )}

      {state.status === 'loading' && <Skeleton />}
      {state.status === 'error' && (
        <ErrorMessage message={`Couldn't load ingredients. ${state.message}`} onRetry={reload} />
      )}
      {state.status === 'ready' && state.data.length === 0 && (
        <div className="card empty">
          <div className="empty-title">
            {debounced ? 'No ingredients match' : 'No ingredients yet'}
          </div>
          {debounced
            ? 'Try a different search.'
            : 'Add the spirits, citrus and syrups you use. New ones also appear as you write recipes.'}
          {!debounced && (
            <div>
              <button className="btn btn-primary" type="button" onClick={() => open()}>
                Add ingredient
              </button>
            </div>
          )}
        </div>
      )}
      {state.status === 'ready' && state.data.length > 0 && (
        <section className="card" aria-label="Ingredients">
          {shown.map((i) => (
            <div key={i.id} className="list-row">
              <div className="grow">
                <div className="row-title truncate">{i.name}</div>
                <div className="row-meta">
                  {i.category} · <span className="num">{i.abvPct}%</span> ABV
                  {i.notes ? ` · ${i.notes}` : ''}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" type="button" onClick={() => open(i)}>
                Edit
              </button>
              <button className="btn btn-plain btn-sm" type="button" onClick={() => setRemoving(i)}>
                Archive
              </button>
            </div>
          ))}
        </section>
      )}

      <dialog
        ref={dialogRef}
        onClose={() => {
          // Ignore a stale close event if the dialog was reopened meanwhile.
          if (!dialogRef.current?.open) setDraft(null);
        }}
      >
        {draft && (
          <form onSubmit={(e) => void submit(e)} noValidate>
            <h2>{draft.id ? 'Edit ingredient' : 'Add ingredient'}</h2>
            <div className="field">
              <label htmlFor="in-name">Name</label>
              <input
                id="in-name"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                autoComplete="off"
                aria-invalid={!!errors.name}
              />
              {errors.name && <div className="field-error">{errors.name}</div>}
            </div>
            <div className="row row-2">
              <div>
                <label htmlFor="in-cat">Category</label>
                <select
                  id="in-cat"
                  value={draft.category}
                  onChange={(e) =>
                    setDraft({ ...draft, category: e.target.value as IngredientCategory })
                  }
                >
                  {INGREDIENT_CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="in-abv">ABV %</label>
                <input
                  id="in-abv"
                  className="num"
                  inputMode="decimal"
                  value={draft.abv}
                  onChange={(e) => setDraft({ ...draft, abv: e.target.value })}
                  placeholder="0"
                  aria-invalid={!!errors.abvPct}
                />
                {errors.abvPct && <div className="field-error">{errors.abvPct}</div>}
              </div>
            </div>
            <div className="field">
              <label htmlFor="in-notes">Notes</label>
              <input
                id="in-notes"
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="actions">
              <button className="btn btn-ghost" type="button" onClick={() => setDraft(null)}>
                Cancel
              </button>
              <span className="spacer" />
              <button className="btn btn-primary" type="submit" disabled={busy} aria-busy={busy}>
                {busy ? 'Saving…' : draft.id ? 'Save changes' : 'Add ingredient'}
              </button>
            </div>
          </form>
        )}
      </dialog>

      <ConfirmDialog
        open={removing !== null}
        title={`Archive ${removing?.name ?? ''}?`}
        body="It won't appear in suggestions any more. Recipes that already use it keep it."
        confirmLabel="Archive ingredient"
        danger={false}
        onCancel={() => setRemoving(null)}
        onConfirm={() => {
          const target = removing;
          setRemoving(null);
          if (!target) return;
          api.archiveIngredient(target.id).then(
            () => {
              toast('Ingredient archived');
              reload();
            },
            (err: unknown) =>
              toast(err instanceof Error ? err.message : 'Something went wrong.', { error: true }),
          );
        }}
      />
    </div>
  );
}
